import { BrighteFooter } from '@components/Brighte';
import { CategoryComponent } from '@graphql';
import { Footer as CBAFooter } from '@pages/Cba/shared';
import { Footer as Electrify2515Footer } from '@pages/Electrify2515/shared/Footer';
import { ENTERPRISE_PARTNER } from '@utils/constants';
import BatteryStorageIcon from '../assets/icons/battery-storage.svg?react';
import ElectricHeatingCoolingSystemsReverseCycleIcon from '../assets/icons/electric-heating-cooling-systems-reverse-cycle.svg?react';
import ElectricResistanceHotWaterSystemIcon from '../assets/icons/electric-resistance-hot-water-system.svg?react';
import EfficientElectricStoveTopsIcon from '../assets/icons/electric-stove-top.svg?react';
import EvChargingInfrastructureIcon from '../assets/icons/ev-charger.svg?react';
import HeatPumpSpaceHeaterIcon from '../assets/icons/heat-pump-space-heater.svg?react';
import HeatPumpWaterHeaterIcon from '../assets/icons/heat-pump-water-heater.svg?react';
import HotWaterHeatPumpsIcon from '../assets/icons/hot-water-heat-pumps-hwhp.svg?react';
import InductionCooktopIcon from '../assets/icons/induction-cooktop.svg?react';
import SmartEnergyDeviceIcon from '../assets/icons/smart-energy-device.svg?react';
import SolarBatterySystemIcon from '../assets/icons/solar-battery-system.svg?react';
import SolarSystemIcon from '../assets/icons/solar-system.svg?react';
import SwitchboardUpgradeIcon from '../assets/icons/switchboard-upgrade.svg?react';
// partner logo
import BrighteCBALogo from '../assets/logos/cba.svg?react';
import E2515Logo from '../assets/logos/electrify-2515.svg?react';
import RACQLogo from '../assets/logos/racq.svg?react';

export const getDetailsField = (financialProductSlug?: string) => {
  switch (financialProductSlug) {
    // case FINANCIAL_PRODUCT.ELECTRIFY_2515:
    //   return ['Device cost: ', 'Maximum subsidy: ', 'Deposit: '];
    default:
      return [];
  }
};

type CategoryComponentType = Omit<
  CategoryComponent,
  | '__typename'
  | 'batteryManufacturerSlug'
  | 'inverterManufacturerSlug'
  | 'pvPanelManufacturerSlug'
  | 'existingInsulationRValue'
  | 'newInsulationRValue'
  | 'totalInsulationRValue'
> & {
  insulationLocation: Pick<CategoryComponent, 'ceiling' | 'floor' | 'walls'>;
};
type UnitsMapType = Record<
  keyof Pick<
    CategoryComponent,
    'pvPanelCapacity' | 'inverterCapacity' | 'heatingCoolingSystemCapacity' | 'batteryCapacity' | 'hwhpTankCapacity'
  >,
  string
>;
type CategoryLabelMapType = Record<keyof CategoryComponentType, string>;
type ManufacturerMapType = Record<
  keyof Pick<
    CategoryComponent,
    'pvPanelCapacity' | 'inverterCapacity' | 'batteryCapacity' | 'heatingCoolingSystemCapacity'
  >,
  string
>;

const categoryLabelMapType: CategoryLabelMapType = {
  pvPanelCapacity: 'Panels',
  inverterCapacity: 'Inverter',
  batteryCapacity: 'Battery',
  insulationType: 'Insulation type',
  insulationLocation: 'Insulation location',
  walls: 'Walls',
  ceiling: 'Ceiling',
  floor: 'Floor',
  heatingCoolingSystemType: 'Heating cooling system type',
  heatingCoolingSystemCapacity: 'Heating cooling system capacity',
  hwhpTankCapacity: 'Tank capacity',
  stoveType: 'Stove type',
  // existingInsulationRValue: 'Old insulation R-Value',
  // newInsulationRValue: 'New insulation R-Value',
  // totalInsulationRValue: 'Total R-Value',
};
const unitsMap: UnitsMapType = {
  pvPanelCapacity: 'kW',
  inverterCapacity: 'kW',
  heatingCoolingSystemCapacity: 'kW',
  batteryCapacity: 'kWh',
  hwhpTankCapacity: 'litre',
};

const deSlug = (slug?: string) => {
  if (!slug) {
    return '';
  }

  const words = slug.split('-');
  return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};
const getUnits = (categoryComponent: keyof UnitsMapType) => unitsMap[categoryComponent] || '';

/**
 * from a category component object get each category present
 * @param categoryComponents
 * @returns map of each category
 */
export const getCategoryItems = (categoryComponents: CategoryComponent) => {
  const {
    pvPanelManufacturerSlug,
    inverterManufacturerSlug,
    batteryManufacturerSlug,
    heatingCoolingSystemType,
    ceiling,
    floor,
    walls,
    ...components
  } = categoryComponents;

  const insulationLocation = {
    ceiling,
    floor,
    walls,
  };

  const itemManufacturer: ManufacturerMapType = {
    pvPanelCapacity: pvPanelManufacturerSlug ?? '',
    inverterCapacity: inverterManufacturerSlug ?? '',
    batteryCapacity: batteryManufacturerSlug ?? '',
    heatingCoolingSystemCapacity: heatingCoolingSystemType ?? '',
  };

  const componentItems = {
    ...components,
    ...(Object.values(insulationLocation).includes(true) && {
      insulationLocation,
    }),
  };

  return Object.entries(componentItems)
    .filter(item => item[1] !== null && categoryLabelMapType[item[0] as keyof CategoryLabelMapType])
    .map(([key, value]) => {
      return {
        label: categoryLabelMapType[key as keyof CategoryLabelMapType],
        value: formatPurchaseItem({
          value,
          unit: getUnits(key as keyof UnitsMapType),
          manufacturerSlug: itemManufacturer[key as keyof ManufacturerMapType],
        }),
      };
    });
};

/**
 * format category item into readable format
 * @param value, unit, manufacturerSlug
 * @returns formatted description of a category item
 */
export const formatPurchaseItem = ({
  value,
  unit = '',
  manufacturerSlug = '',
}: {
  value: string | number | null | Pick<CategoryComponent, 'ceiling' | 'floor' | 'walls'>;
  unit: string;
  manufacturerSlug: string;
}) => {
  if (value && typeof value === 'object') {
    let purchaseItem = '';

    Object.entries(value).forEach(([key, value]) => {
      if (value) {
        purchaseItem += `${categoryLabelMapType[key as keyof CategoryLabelMapType]}, `;
      }
    });

    purchaseItem = purchaseItem.slice(0, -2);
    return purchaseItem;
  }

  const manufacturer = manufacturerSlug ? `${deSlug(manufacturerSlug)} ${String.fromCodePoint(8226)} ` : '';

  return value ? `${manufacturer}${value} ${unit}` : value;
};

// TODO: GET FROM SPARK WEB - temporary solution
export const getIconsFromSlug = (slug: string) => {
  switch (slug) {
    case 'battery-storage':
      return <BatteryStorageIcon aria-hidden="true" focusable="false" />;
    case 'efficient-electric-stove-tops':
      return <EfficientElectricStoveTopsIcon aria-hidden="true" focusable="false" />;
    case 'electric-heating-cooling-systems-reverse-cycle':
      return <ElectricHeatingCoolingSystemsReverseCycleIcon />;
    case 'electric-resistance-hot-water-system':
      return <ElectricResistanceHotWaterSystemIcon />;
    case 'ev-charging-infrastructure':
      return <EvChargingInfrastructureIcon aria-hidden="true" focusable="false" />;
    case 'heat-pump-space-heater':
      return <HeatPumpSpaceHeaterIcon />;
    case 'heat-pump-water-heater':
      return <HeatPumpWaterHeaterIcon />;
    case 'hot-water-heat-pumps-hwhp':
      return <HotWaterHeatPumpsIcon aria-hidden="true" focusable="false" />;
    case 'induction-cooktop':
      return <InductionCooktopIcon />;
    case 'smart-energy-device':
      return <SmartEnergyDeviceIcon />;
    case 'solar-battery-system':
      return <SolarBatterySystemIcon />;
    case 'solar-system':
      return <SolarSystemIcon aria-hidden="true" focusable="false" />;
    case 'switchboard-upgrade':
      return <SwitchboardUpgradeIcon />;
  }
};

export const FOOTER_CONFIG = {
  [ENTERPRISE_PARTNER.COMMBANK]: {
    component: CBAFooter,
    stickToBottom: true,
  },
  [ENTERPRISE_PARTNER.REWIRING_AU]: {
    component: Electrify2515Footer,
    stickToBottom: false,
  },
  default: {
    component: BrighteFooter,
    stickToBottom: true,
  },
};

export const getPartnerLogo = (partnerId: string) => {
  switch (partnerId) {
    case ENTERPRISE_PARTNER.REWIRING_AU:
      return <E2515Logo width={140} height={35} />;
    case ENTERPRISE_PARTNER.COMMBANK:
      return <BrighteCBALogo aria-label="Brighte Commbank Logo" />;
    case ENTERPRISE_PARTNER.RACQ:
      return <RACQLogo />;
    case ENTERPRISE_PARTNER.BRIGHTE:
    default:
      return null;
  }
};
